// @flow

import React, {Component} from 'react';
import Button from '@react/react-spectrum/Button';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';
import DeviceDetectionUtil from '../../../../utils/DeviceDetectionUtil';
import {
	HELP_TIMEOUT,
	AUTO_DOWNLOAD_TIMEOUT,
	GET_HELP_STRINGS,
	LIST_IDS,
	TAGS,
	GET_HELP_OPEN_SOURCES,
	DOWNLOAD_CONTROLLER_USER_SOURCE
} from '../../../../constants/AppPicker/appPickerConstants';
import AppsUtil from '../../../../utils/AppsUtil';
import CSSUtil from '../../../../utils/CSSUtil';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import DownloadPageFooter from '../DownloadPageFooter/DownloadPageFooter';
import IncompatibleOSViewScss from './IncompatibleOSView.scss';
import './../HomeView/HomeView.scss';
import uuid from 'uuid';

const ANALYTICS = {
	PAGE_RENDER: 'page_render',
	PAGE_CTA_CLICK: 'page_cta_click'
};

type Props = {
	environment: string,
	locale: string,
	hasThor: boolean,
	enableHelpTimeouts: boolean,
	clearHelpTimeouts: Function,
	viewYourDesktopAppsLinkClicked: Function,
	imsData: Object,
	history: Object,
	intl: Object,
};

type AnalyticObjectInterface = {
	event?: {
		type?: string,
		subtype?: ?string,
		subcategory?: string,
		guid?: string
	},
	content?: {
		action?: ?string
	},
	ui?: {
		view_type?: ?string
	}
};

export default class IncompatibleOSView extends Component<Props> {
	helpPopoverTimeout: ?TimeoutID;

	constructor(props: Props) {
		super(props);
		this.helpPopoverTimeout = null;
	}

	componentDidMount() {
		if (this.props.enableHelpTimeouts) {
			this.helpPopoverTimeout = setTimeout(() => {
				this.openInactivityGetHelpPopover();
				this.helpPopoverTimeout = setTimeout(() => this.openAutoDownloadPopover(), AUTO_DOWNLOAD_TIMEOUT);
			}, HELP_TIMEOUT);
			CSSUtil.addEventListenerToElem(window, 'click', this.clearInactivityTimeouts);
			CSSUtil.addEventListenerToElem(window, 'keydown', this.clearInactivityTimeouts);
			this.props.clearHelpTimeouts();
		}
		this.handleAnalyticsEvent(ANALYTICS.PAGE_RENDER);
		this.updateHelpPopoverConfig();
	}

	updateHelpPopoverConfig(heading?: string) {
		const updatingList = {
			listId: LIST_IDS.FAQ_AND_HELP,
			heading,
			pageType: [TAGS.PAGE.INCOMPATIBLE_OS_SCREEN],
			compatibilityTags: HelpPopoverUtil.getCompatibilityTags([], true)
		};
		HelpPopoverUtil.updateHelpPopoverConfig(undefined, [updatingList]);
	}

	openAutoDownloadPopover() {
		HelpPopoverUtil.openAutoDownloadPopover();
	}

	openInactivityGetHelpPopover() {
		const heading = this.props.intl.formatMessage({id: GET_HELP_STRINGS.INACTIVITY_FAQ_HEADING});
		this.updateHelpPopoverConfig(heading);
		HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.INACTIVITY);
	}

	componentWillUnmount() {
		this.clearInactivityTimeouts();
	}

	onSystemReqClick = (e: Object) => {
		e.preventDefault();
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(AppPickerUtil.getSystemRequirementLink('KCCC'), undefined, guid);
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'system-requirements', guid, url});
	}

	onGooglePlayStoreClick = (e: Object) => {
		e.preventDefault();
		const url = 'https://play.google.com/store/apps/developer?id=Adobe';
		window.open(url, '_blank');
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'google-play-store', undefined, url});
	}

	handleAnalyticsEvent(eventType: string, params?: Object) {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		let eventObj: AnalyticObjectInterface = {};
		switch (eventType) {
			case ANALYTICS.PAGE_RENDER:
				eventObj = {
					event: {
						type: 'render',
						subtype: 'page'
					}
				};
				break;
			case ANALYTICS.PAGE_CTA_CLICK:
				eventObj = {
					event: {
						type: 'click',
						subtype: params && params.ctaType,
						guid: params && params.guid
					},
					content: {
						action: params && params.url
					}
				};
				break;
		}
		eventObj.event = {
			...eventObj.event,
			subcategory: 'Welcome'
		};
		eventObj.ui = {view_type: analytics.getPageTypeString()}; // eslint-disable-line camelcase
		analytics.genericAnalyticEvent(eventObj);
	}

	clearInactivityTimeouts = () => {
		if (this.helpPopoverTimeout) {
			clearTimeout(this.helpPopoverTimeout);
		}
		window.removeEventListener('click', this.clearInactivityTimeouts);
		window.removeEventListener('keydown', this.clearInactivityTimeouts);
	};

	openChat() {
		window.AdobeMessagingExperienceClient && window.AdobeMessagingExperienceClient.openMessagingWindow({sourceType: 'link'});
	}

	downloadCreativeCloud = () => {
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'install-ccd'});
		const sapcode = 'KCCC';
		const productConfig = AppsUtil.getFFCProductConfigBySapCode(sapcode);
		if (productConfig) {
			const url = AppsUtil.getInstallationModalUrl({
				sapCode: sapcode,
				source: AppPickerUtil.getSourceQueryParam(),
				platform: AppsUtil.getProductPlatform(productConfig)
			});
			if (AppsUtil.hasFeature('temp_cch_10855')) {
				window._uxa = window._uxa || [];
				window._uxa.push(['trackPageview', `${url}&isStarter=true`]);
			}
			this.props.history.push(`${url}&isStarter=true`);
		}
	};

	redirectToCreativeCloudWeb = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(`${window.location.origin}/apps/all/desktop`, undefined, guid, undefined, undefined, '_self');
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'continue-to-creative-cloud', url, guid});
	}

	getDnldCTALabel() {
		return this.props.hasThor
			? 'apps.appPicker.incompatibleOSView.body.ctaText.thor'
			: 'apps.appPicker.incompatibleOSView.body.ctaText.kaizen';
	}

	getBodyTextLabel() {
		if (DeviceDetectionUtil.isChromeOS() || DeviceDetectionUtil.isLinux()) {
			return 'apps.appPicker.incompatibleOSView.body.text.linux-chromeos';
		}
		return this.props.hasThor
			? 'apps.appPicker.incompatibleOSView.body.text.thor'
			: 'apps.appPicker.incompatibleOSView.body.text.kaizen';
	}

	getBodyCTAs() {
		const buttonList = [];
		if (DeviceDetectionUtil.isChromeOS() || DeviceDetectionUtil.isLinux()) {
			buttonList.push(
				<Button
					className={IncompatibleOSViewScss.primaryCTA}
					variant='cta'
					onClick={this.redirectToCreativeCloudWeb}
					label={this.props.intl.formatMessage({id: 'apps.appPicker.incompatibleOSView.body.continueToCCD'})}
				/>
			);
		}
		else {
			buttonList.push(
				<Button
					className={IncompatibleOSViewScss.primaryCTA}
					variant='cta'
					onClick={this.downloadCreativeCloud}
					disabled={!AppsUtil.getFFCProductConfigBySapCode('KCCC')}
					label={this.props.intl.formatMessage({id: this.getDnldCTALabel()})}
				/>
			);
		}
		if (DeviceDetectionUtil.isChromeOS()) {
			buttonList.push(
				<Button
					className={IncompatibleOSViewScss.secondaryCTA}
					variant='primary'
					quiet
					onClick={this.onGooglePlayStoreClick}
					label={this.props.intl.formatMessage({id: 'apps.appPicker.incompatibleOSView.body.googlePlayStore'})}
				/>
			);
		}
		else {
			buttonList.push(
				<Button
					className={IncompatibleOSViewScss.secondaryCTA}
					variant='primary'
					quiet
					onClick={this.onSystemReqClick}
					label={this.props.intl.formatMessage({id: 'apps.appPicker.incompatibleOSView.body.sysReqts'})}
				/>
			);
		}
		return buttonList;
	}

	renderBodySection = () => (
		<div className={IncompatibleOSViewScss.bodySection} key='incompatible-os-view-body'>
			<div data-cs-mask='' className={IncompatibleOSViewScss.heading}>
				{this.props.intl.formatMessage({
					id: 'apps.appPicker.incompatibleOSView.body.heading'},
				{username: this.props.imsData.name}
				)}
				<br></br>
				{this.props.intl.formatMessage({id: 'apps.appPicker.incompatibleOSView.body.subHeading'})}
			</div>
			<div className={IncompatibleOSViewScss.text}>
				<span>
					{this.props.intl.formatMessage({
						id: this.getBodyTextLabel()
					})}
				</span>
			</div>
			<div className={IncompatibleOSViewScss.buttons}>
				{this.getBodyCTAs()}
			</div>
		</div>
	);

	renderFooterSection = () => {
		const {hasThor, environment, intl, viewYourDesktopAppsLinkClicked} = this.props;
		return (
			<DownloadPageFooter
				hasThor={hasThor}
				environment={environment}
				source={DOWNLOAD_CONTROLLER_USER_SOURCE.INCOMPATIBLE_OS_SCREEN}
				formatMessage={intl.formatMessage}
				viewYourDesktopAppsLinkClicked={viewYourDesktopAppsLinkClicked}
			/>
		);
	};

	render() {
		return (
			<div id='app-picker-incompatible-os-view' className={IncompatibleOSViewScss.incompatibleUIContainer}>
				<div className={IncompatibleOSViewScss.bodySectionWrapper}>
					{this.renderBodySection()}
				</div>
				<div className={IncompatibleOSViewScss.footerSectionWrapper}>
					{this.renderFooterSection()}
				</div>
			</div>
		);
	}
}
